///
/// EarthBrowser
///
/// Created by Matt Giger on 9/6/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type rect } from "@stovokor/math/rect"
import { type BlendFactor } from "@stovokor/shader/GLConstants"
import { StovokorContext } from "@stovokor/StovokorContext"

export function emptyAction() { }
export type ShaderAction = () => void
export type ShaderActions = ShaderAction[]

export interface ActionContainer {
	action: ShaderAction
}


export const depthTest = (context: StovokorContext, enabled: boolean) => {
	return () => {
		context.glState.setEnabled(WebGL2RenderingContext.DEPTH_TEST, enabled)
		context.glState.depthMask(enabled)
	}
}

export const depthMask = (context: StovokorContext, enabled: boolean) => {
	return () => {
		context.glState.depthMask(enabled)
	}
}

/**
 * Pre/post action pair that sets the depth write mask and restores the
 * previous value afterwards. Use this instead of a depthMask(true) restore:
 * the transparent pass runs with depth writes globally disabled, and blindly
 * re-enabling them mid-pass lets later transparent objects self-occlude
 * (e.g. banding on two-sided fresnel spheres).
 */
export const scopedDepthMask = (context: StovokorContext, enabled: boolean): { pre: ShaderAction, post: ShaderAction } => {
	let saved = enabled
	return {
		pre: () => {
			saved = context.glState.depthWriteMask
			context.glState.depthMask(enabled)
		},
		post: () => {
			context.glState.depthMask(saved)
		}
	}
}

export const cullFace = (context: StovokorContext, enabled: boolean) => {
	return () => {
		context.glState.setEnabled(WebGL2RenderingContext.CULL_FACE, enabled)
	}
}

// clipRect and height are logical (CSS) pixels; gl.scissor wants device pixels
export const enableScissorTest = (context: StovokorContext, clipRect: rect, height: number): () => void => {
	return () => {
		const ratio = context.pixelRatio
		context.glState.enable(WebGL2RenderingContext.SCISSOR_TEST)
		context.gl.scissor(
			Math.round(clipRect.left * ratio),
			Math.round((height - clipRect.bottom) * ratio),
			Math.round(clipRect.width * ratio),
			Math.round(clipRect.height * ratio))
	}
}

export const disableScissorTest = (context: StovokorContext): () => void => {
	return () => {
		context.glState.disable(WebGL2RenderingContext.SCISSOR_TEST)
	}
}

export const blendFunc = (context: StovokorContext, sfactor: BlendFactor, dfactor: BlendFactor) => {
	return () => {
		context.glState.blendFunc(sfactor, dfactor)
	}
}
